import { useCallback, useEffect, useState } from 'react';
import { Download, LogOut, Printer, FileText, BookOpen, Wallet, Coins } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchRelevesClients,
  fetchCuves,
  enregistrerRetrait,
  fetchComptesTiers,
  fetchContacts,
} from '../lib/dataProvider';
import { RechercheContacts, correspondContact } from '../components/shared/ContactCombobox';
import { fmt, PageTitle, StatutBadge, StatBlock } from '../components/shared/badges';
import { couleurLigneTiers } from '../lib/couleurs';
import { ConversionDialog } from '../components/stocks/ConversionDialog';
import { fetchDroitsHuile } from '../lib/risqueHuile';
import { FactureTrituration } from '../components/releves/FactureTrituration';
import { GrandLivreTiers } from '../components/releves/GrandLivreTiers';
import { BonSortieHuile } from '../components/releves/BonSortieHuile';
import { PaiementTiersDialog } from '../components/reception/PaiementTiersDialog';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const inputCls =
  'h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white sm:h-10';

const ONGLETS = [
  { id: 'comptes', label: 'Comptes courants (argent)' },
  { id: 'creances', label: 'Créances & dettes' },
  { id: 'huile', label: 'Huile stockée' },
];

const joursDepuis = (d) => Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 86400000));

// Ancienneté = date de la plus vieille opération non réglée du compte.
const plusAncienneOperation = (c, sens) => {
  const lignes = c.lignes.filter((l) => (sens === 'creance' ? l.debit > 0 : l.credit > 0));
  return lignes.length ? lignes.reduce((min, l) => (l.date < min ? l.date : min), lignes[0].date) : c.lignes[0]?.date ?? '—';
};

export default function Releves() {
  const [onglet, setOnglet] = useState('comptes');
  const [comptes, setComptes] = useState([]);
  const [releves, setReleves] = useState([]);
  const [cuves, setCuves] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [recherche, setRecherche] = useState('');
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [retrait, setRetrait] = useState({ date: aujourdhui, client: '', cuveId: '', litres: '' });
  const [facture, setFacture] = useState(null);
  const [livre, setLivre] = useState(null);
  const [conversionTiers, setConversionTiers] = useState(null);
  const [paiement, setPaiement] = useState(null);
  const [bonSortie, setBonSortie] = useState(null);
  const [periode, setPeriode] = useState({ du: '', au: '' });

  const appliquerPeriode = (c) => {
    if (!periode.du) return { ...c, report: null };
    const dans = (d) => (!periode.du || d >= periode.du) && (!periode.au || d <= periode.au);
    const report = c.lignes
      .filter((l) => periode.du && l.date < periode.du)
      .reduce((s, l) => s + l.credit - l.debit, 0);
    return { ...c, lignes: c.lignes.filter((l) => dans(l.date)), report: Number(report.toFixed(3)) };
  };

  const load = useCallback(() => {
    fetchComptesTiers().then(setComptes);
    fetchRelevesClients().then(setReleves);
    fetchCuves().then(setCuves);
    fetchContacts().then(setContacts);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalDepose = releves.reduce((s, r) => s + r.depose, 0);
  const totalSolde = releves.reduce((s, r) => s + r.solde, 0);
  const totalFrais = releves.reduce((s, r) => s + r.fraisTrituration, 0);
  const aPayer = comptes.filter((c) => c.solde > 0).reduce((s, c) => s + c.solde, 0);
  const aRecevoir = comptes.filter((c) => c.solde < 0).reduce((s, c) => s - c.solde, 0);

  // Créances (le tiers me doit) et dettes (je dois au tiers), du plus ancien au plus récent.
  const creances = comptes
    .filter((c) => c.solde < -0.001)
    .map((c) => ({ ...c, montant: -c.solde, depuis: plusAncienneOperation(c, 'creance') }))
    .sort((a, b) => String(a.depuis).localeCompare(String(b.depuis)));
  const dettes = comptes
    .filter((c) => c.solde > 0.001)
    .map((c) => ({ ...c, montant: c.solde, depuis: plusAncienneOperation(c, 'dette') }))
    .sort((a, b) => String(a.depuis).localeCompare(String(b.depuis)));

  // Filtre d'affichage uniquement : mêmes champs que la recherche du combobox contacts
  // (nom, code, codeClient/codeFournisseur, téléphone, MF, CIN, région).
  const correspond = (tiersId, nom) =>
    !recherche || correspondContact({ ...(contacts.find((c) => c.id === tiersId) ?? {}), nom }, recherche);
  const comptesVus = comptes.filter((c) => correspond(c.tiersId, c.nom));
  const creancesVues = creances.filter((c) => correspond(c.tiersId, c.nom));
  const dettesVues = dettes.filter((c) => correspond(c.tiersId, c.nom));
  const relevesVus = releves.filter((r) => correspond(r.tiersId, r.nom));
  const totalOnglet = onglet === 'huile' ? releves.length : onglet === 'creances' ? creances.length + dettes.length : comptes.length;
  const vusOnglet = onglet === 'huile' ? relevesVus.length : onglet === 'creances' ? creancesVues.length + dettesVues.length : comptesVus.length;

  const exportBalance = () => {
    const lignes = [
      'Type;Tiers;Categorie;Depuis;Age (jours);Montant (TND)',
      ...creances.map((c) => `Créance client;${c.nom};${c.type ?? ''};${c.depuis};${joursDepuis(c.depuis)};${c.montant.toFixed(3)}`),
      ...dettes.map((c) => `Dette fournisseur;${c.nom};${c.type ?? ''};${c.depuis};${joursDepuis(c.depuis)};${c.montant.toFixed(3)}`),
    ];
    const url = URL.createObjectURL(new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'creances-et-dettes.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Balance âgée exportée');
  };

  const submit = async (e) => {
    e.preventDefault();
    const { error, row, avertissement } = await enregistrerRetrait(retrait);
    if (error) return toast.error(error);
    const cuve = cuves.find((c) => c.id === retrait.cuveId);
    setBonSortie({
      ...row,
      cuveNom: row.cuveNom ?? cuve?.nom ?? '—',
      solde: row.soldeApres ?? 0,
    });
    toast.success(`Retrait de ${fmt(retrait.litres)} Kg enregistré — bon de sortie généré`);
    if (avertissement) toast.warning(avertissement, { duration: 8000 });
    setRetrait({ ...retrait, litres: '' });
    load();
  };

  const exportCompte = (c) => {
    const head = ['Date', 'Opération', 'N° Bon/Pièce', 'Brut', 'Tare', 'Net', 'Rdt théorique %', 'Prix base', 'Débit', 'Crédit', 'Solde', 'Notes'];
    const lignes = c.lignes.map((l) =>
      [l.date, l.type, l.bon, l.poidsBrut, l.tare, l.poidsNet, l.rendementTheorique ?? '', l.prixBase, l.debit.toFixed(3), l.credit.toFixed(3), l.solde.toFixed(3), l.notes].join(';')
    );
    const url = URL.createObjectURL(
      new Blob([[`Relevé de compte;${c.nom}`, head.join(';'), ...lignes, `Solde;${c.solde.toFixed(3)}`].join('\n')], {
        type: 'text/csv;charset=utf-8;',
      })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `compte_${c.nom.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Relevé exporté');
  };

  return (
    <div data-testid="releves-page" className="space-y-6">
      <PageTitle
        emoji="📄"
        titre="Relevés de comptes"
        sous="Grand livre unifié par tiers : réceptions, prestations, paiements et avances"
        action={
          <button
            type="button"
            data-testid="imprimer-releves"
            onClick={() => window.print()}
            className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
          >
            <Printer size={15} /> Imprimer
          </button>
        }
      />

      <div className="flex flex-wrap gap-px bg-slate-200">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`onglet-${o.id}`}
            onClick={() => setOnglet(o.id)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ${
              onglet === o.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <RechercheContacts
        valeur={recherche}
        onChange={setRecherche}
        testid="recherche-tiers"
        placeholder="Rechercher un tiers : nom, code client / fournisseur, téléphone…"
        indication={recherche ? `${vusOnglet} / ${totalOnglet}` : `${totalOnglet} tiers`}
      />

      {onglet === 'creances' && (
        <div data-testid="onglet-creances-contenu" className="space-y-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <StatBlock testid="stat-creances" titre="Créances clients" accent="border-l-emerald-600" items={[['Tiers', fmt(creances.length)], ['Total (TND)', fmt(aRecevoir, 3)]]} />
            <StatBlock testid="stat-dettes" titre="Dettes fournisseurs" accent="border-l-orange-500" items={[['Tiers', fmt(dettes.length)], ['Total (TND)', fmt(aPayer, 3)]]} />
            <StatBlock testid="stat-position-nette" titre="Position nette" accent="border-l-slate-900" items={[[aRecevoir - aPayer >= 0 ? 'En ma faveur (TND)' : 'À mon débit (TND)', fmt(Math.abs(aRecevoir - aPayer), 3)]]} />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="export-balance-agee"
              onClick={exportBalance}
              className="flex h-10 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Download size={15} /> Export CSV (balance âgée)
            </button>
          </div>

          {[
            {
              id: 'creances',
              titre: 'Ils me doivent de l’argent — créances clients',
              hint: 'Du plus ancien au plus récent : factures à crédit, prestations de trituration et avances non récupérées',
              accent: 'border-l-emerald-600',
              rows: creancesVues,
            },
            {
              id: 'dettes',
              titre: 'Je leur dois de l’argent — fournisseurs & agriculteurs',
              hint: 'Du plus ancien au plus récent : achats d’olives et d’huile non réglés, salaires dus',
              accent: 'border-l-orange-500',
              rows: dettesVues,
            },
          ].map((bloc) => (
            <section key={bloc.id} data-testid={`bloc-${bloc.id}`} className={`border border-slate-200 border-l-2 ${bloc.accent} bg-white`}>
              <div className="border-b border-slate-200 px-4 py-3">
                <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">{bloc.titre}</h2>
                <p className="text-xs text-slate-500">{bloc.hint}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left" data-testid={`table-${bloc.id}`}>
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      {['Ancienneté', 'Âge', 'Tiers', 'Catégorie', 'Détail du compte', 'Montant (TND)', 'Actions'].map((h) => (
                        <th key={h} className={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bloc.rows.length === 0 ? (
                      <tr data-testid={`${bloc.id}-empty`}>
                        <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500">
                          {recherche
                            ? `Aucun tiers ne correspond à « ${recherche} ».`
                            : bloc.id === 'creances'
                              ? 'Aucun client débiteur — tout est encaissé.'
                              : 'Aucune dette en cours — tous les tiers sont réglés.'}
                        </td>
                      </tr>
                    ) : (
                      bloc.rows.map((c) => {
                        const age = joursDepuis(c.depuis);
                        return (
                          <tr key={c.tiersId} data-testid={`${bloc.id}-row-${c.tiersId}`} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-data text-sm text-slate-700">{c.depuis}</td>
                            <td className="px-3 py-2">
                              <span
                                data-testid={`age-${c.tiersId}`}
                                className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${
                                  age > 90 ? 'bg-red-50 text-red-700' : age > 30 ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {age} j
                              </span>
                            </td>
                            <td className="px-3 py-2 text-sm font-semibold text-slate-900">{c.nom}</td>
                            <td className="px-3 py-2 text-sm text-slate-700">{c.type ?? '—'}</td>
                            <td className="px-3 py-2 font-data text-[11px] text-slate-500">
                              Achats {fmt(c.totalAchats, 3)} · Prestations {fmt(c.totalPrestations, 3)} · Payé {fmt(c.totalPaye, 3)} · Encaissé {fmt(c.totalEncaisse, 3)}
                            </td>
                            <td data-testid={`montant-${c.tiersId}`} className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">
                              {fmt(c.montant, 3)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex justify-end gap-1.5">
                                <button
                                  type="button"
                                  data-testid={`balance-regler-${c.tiersId}`}
                                  onClick={() => setPaiement(c.tiersId)}
                                  className="inline-flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                                >
                                  <Wallet size={13} /> {bloc.id === 'creances' ? 'Encaisser' : 'Régler'}
                                </button>
                                <button
                                  type="button"
                                  data-testid={`balance-livre-${c.tiersId}`}
                                  onClick={() => setLivre(c)}
                                  className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                                >
                                  <BookOpen size={13} /> Relevé
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {onglet === 'comptes' && (
        <>
          <div data-testid="filtre-periode" className="flex flex-col gap-3 border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-end">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Du</label>
              <input data-testid="periode-du" type="date" value={periode.du} onChange={(e) => setPeriode((p) => ({ ...p, du: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Au</label>
              <input data-testid="periode-au" type="date" value={periode.au} onChange={(e) => setPeriode((p) => ({ ...p, au: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
            </div>
            <button
              type="button"
              data-testid="periode-reset"
              onClick={() => setPeriode({ du: '', au: '' })}
              className="h-11 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
            >
              Toute la campagne
            </button>
            <p className="font-data text-[11px] text-slate-500">
              Extrait de compte filtré par période — le solde antérieur est repris en « report ».
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <StatBlock testid="stat-comptes-tiers" titre="Comptes tiers actifs" accent="border-l-sky-600" items={[['Contacts', fmt(comptes.length)]]} />
            <StatBlock testid="stat-a-payer" titre="À payer aux tiers" accent="border-l-orange-500" items={[['Total (TND)', fmt(aPayer, 3)]]} />
            <StatBlock testid="stat-a-recevoir" titre="À encaisser des tiers" accent="border-l-emerald-600" items={[['Total (TND)', fmt(aRecevoir, 3)]]} />
          </div>

          {comptes.length === 0 ? (
            <p data-testid="comptes-empty" className="border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
              Aucun compte tiers mouvementé.
            </p>
          ) : comptesVus.length === 0 ? (
            <p data-testid="comptes-recherche-vide" className="border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
              Aucun tiers ne correspond à « {recherche} ».
            </p>
          ) : (
            comptesVus.map((brut) => {
              const c = appliquerPeriode(brut);
              return (
              <section key={c.tiersId} data-testid={`compte-${c.tiersId}`} className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
                <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">{c.nom}</h2>
                    <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                      Achats {fmt(c.totalAchats, 3)} · Prestations {fmt(c.totalPrestations, 3)} · Payé{' '}
                      {fmt(c.totalPaye, 3)} · Encaissé {fmt(c.totalEncaisse, 3)} TND
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                        {c.solde > 0 ? 'À payer au tiers' : c.solde < 0 ? 'Dû par le tiers' : 'Compte soldé'}
                      </p>
                      <p data-testid={`solde-compte-${c.tiersId}`} className="tabular font-data text-2xl font-medium text-slate-900">
                        {fmt(Math.abs(c.solde), 3)} <span className="text-sm text-slate-500">TND</span>
                      </p>
                    </div>
                    <button type="button" data-testid={`regler-${c.tiersId}`} onClick={() => setPaiement(c.tiersId)} className="flex h-11 items-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10">
                      <Wallet size={15} /> Régler / Avance
                    </button>
                    <button type="button" data-testid={`convertir-huile-${c.tiersId}`} onClick={() => setConversionTiers(c.tiersId)} className="flex h-11 items-center gap-2 border-2 border-amber-600 bg-amber-50 px-3 text-sm font-bold text-amber-900 transition-colors duration-150 hover:bg-amber-600 hover:text-white sm:h-10">
                      <Coins size={15} /> Convertir huile → dette
                    </button>
                    <button type="button" data-testid={`livre-${c.tiersId}`} onClick={() => setLivre(c)} className="flex h-11 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10">                      <BookOpen size={15} /> Relevé imprimable
                    </button>
                    <button type="button" data-testid={`export-compte-${c.tiersId}`} onClick={() => exportCompte(c)} className="flex h-11 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10">
                      <Download size={15} /> CSV
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left" data-testid={`livre-table-${c.tiersId}`}>
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {['Date', 'Opération', 'N° Bon / Pièce', 'Brut (Kg)', 'Tare', 'Net (Kg)', 'Rdt (%)', 'Prix base', 'Débit (TND)', 'Crédit (TND)', 'Solde', 'Notes / Justificatif'].map((h) => (
                          <th key={h} className={th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {c.report !== null && (
                        <tr data-testid={`report-${c.tiersId}`} className="border-b border-slate-200 bg-slate-100">
                          <td className="px-3 py-2 font-data text-sm text-slate-700">{periode.du || '—'}</td>
                          <td className="px-3 py-2 text-sm font-semibold text-slate-800" colSpan={9}>
                            Report du solde antérieur
                          </td>
                          <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(c.report, 3)}</td>
                          <td className="px-3 py-2 text-sm text-slate-600">—</td>
                        </tr>
                      )}
                      {c.lignes.map((l, i) => (
                        <tr key={`${l.nature}-${l.id}-${i}`} data-testid={`ligne-${l.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${l.nature === 'paiement' || l.nature === 'encaissement' ? 'bg-slate-50/60' : ''}`}>
                          <td className="px-3 py-2 font-data text-sm text-slate-700">{l.date}</td>
                          <td className="px-3 py-2 text-sm text-slate-800">{l.type}</td>
                          <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{l.bon}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{l.poidsBrut ? fmt(l.poidsBrut) : '—'}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{l.poidsBrut ? fmt(l.tare) : '—'}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{l.poidsNet ? fmt(l.poidsNet) : '—'}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{l.rendement ? fmt(l.rendement, 2) : '—'}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{l.prixBase ? fmt(l.prixBase, 3) : '—'}</td>
                          <td className={`tabular px-3 py-2 font-data text-sm ${couleurLigneTiers(l.nature, 'debit')}`}>{l.debit ? fmt(l.debit, 3) : '—'}</td>
                          <td className={`tabular px-3 py-2 font-data text-sm ${couleurLigneTiers(l.nature, 'credit')}`}>{l.credit ? fmt(l.credit, 3) : '—'}</td>
                          <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(l.solde, 3)}</td>
                          <td className="px-3 py-2 text-sm text-slate-600">{l.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
              );
            })
          )}
        </>
      )}

      {onglet === 'huile' && (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <StatBlock testid="stat-comptes" titre="Comptes huile ouverts" accent="border-l-sky-600" items={[['Tiers', fmt(releves.length)]]} />
            <StatBlock
              testid="stat-huile-tiers"
              titre="Huile au compte des tiers"
              accent="border-l-emerald-600"
              items={[
                ['Déposée (Kg)', fmt(totalDepose, 1)],
                ['Solde en cuve (Kg)', fmt(totalSolde, 1)],
              ]}
            />
            <StatBlock testid="stat-frais" titre="Frais de trituration" accent="border-l-amber-600" items={[['Facturés (TND)', fmt(totalFrais, 3)]]} />
          </div>

          <section data-testid="section-retrait" className="border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
                <LogOut size={16} /> Enregistrer un retrait d'huile
              </h2>
              <p className="text-xs text-slate-500">Sortie d'huile du compte tiers — décrémente la cuve concernée</p>
            </div>
            <form onSubmit={submit} className="grid grid-cols-1 gap-3 bg-slate-50 px-4 py-3 md:grid-cols-5">
              <input data-testid="retrait-date" type="date" value={retrait.date} onChange={(e) => setRetrait({ ...retrait, date: e.target.value })} className={inputCls} />
              <select data-testid="retrait-client" value={retrait.client} onChange={(e) => setRetrait({ ...retrait, client: e.target.value })} className={inputCls}>
                <option value="">— Tiers —</option>
                {releves.map((r) => (
                  <option key={r.nom} value={r.nom}>
                    {r.nom} ({fmt(r.solde, 1)} Kg)
                  </option>
                ))}
              </select>
              <select data-testid="retrait-cuve" value={retrait.cuveId} onChange={(e) => setRetrait({ ...retrait, cuveId: e.target.value })} className={inputCls}>
                <option value="">— Cuve —</option>
                {cuves
                  .filter((c) => !c.virtuelle && !c.verrouillee && c.id !== 'CUVE_SORTIE_DIRECTE')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nom} · {fmt(c.volume)} {c.unite}
                    </option>
                  ))}
              </select>
              <input data-testid="retrait-litres" type="number" step="0.1" placeholder="Kg retirés" value={retrait.litres} onChange={(e) => setRetrait({ ...retrait, litres: e.target.value })} className={inputCls} />
              <button type="submit" data-testid="retrait-submit" className="flex h-11 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10">
                Enregistrer le retrait
              </button>
            </form>
          </section>

          {releves.length === 0 ? (
            <p data-testid="releves-empty" className="border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
              Aucun compte huile alimenté. Les dépôts apparaissent dès qu'un lot client trituré est stocké en cuve.
            </p>
          ) : relevesVus.length === 0 ? (
            <p data-testid="releves-recherche-vide" className="border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
              Aucun tiers ne correspond à « {recherche} ».
            </p>
          ) : (
            relevesVus.map((r) => (
              <section key={r.nom} data-testid={`releve-${r.nom}`} className="border border-slate-200 border-l-2 border-l-sky-600 bg-white">
                <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">{r.nom}</h2>
                    <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                      {r.depots.length} dépôt{r.depots.length > 1 ? 's' : ''} · {r.retraits.length} retrait
                      {r.retraits.length > 1 ? 's' : ''} · frais {fmt(r.fraisTrituration, 3)} TND
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-6">
                    <div className="text-right">
                      <p className="text-[11px] text-slate-500">Déposé (Kg)</p>
                      <p className="tabular font-data text-lg font-semibold text-slate-900">{fmt(r.depose, 1)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-slate-500">Retiré (Kg)</p>
                      <p className="tabular font-data text-lg font-semibold text-slate-900">{fmt(r.retire, 1)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-sky-700">Solde en cuve</p>
                      <p data-testid={`solde-${r.nom}`} className="tabular font-data text-2xl font-medium text-slate-900">
                        {fmt(r.solde, 1)} <span className="text-sm text-slate-500">Kg</span>
                      </p>
                    </div>
                    <button type="button" data-testid={`facture-${r.nom}`} onClick={() => setFacture(r)} className="flex h-11 items-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10">
                      <FileText size={15} /> Facture trituration
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {['Mouvement', 'N° Bon', 'Date', 'Cuve', 'Olives (Kg)', 'Rendement (%)', 'Huile (Kg)', 'Statut', 'Document'].map((c) => (
                          <th key={c} className={th}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {r.depots.map((d) => (
                        <tr key={d.id} data-testid={`depot-${d.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-data text-xs uppercase tracking-[0.06em] text-emerald-700">Dépôt</td>
                          <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{d.id}</td>
                          <td className="px-3 py-2 font-data text-sm text-slate-700">{d.date}</td>
                          <td className="px-3 py-2 text-sm text-slate-700">{d.cuve}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(d.olives)}</td>
                          <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(d.rendement, 2)}</td>
                          <td className="tabular px-3 py-2 font-data text-sm font-semibold text-emerald-700">+{fmt(d.huile, 1)}</td>
                          <td className="px-3 py-2"><StatutBadge statut={d.statut} /></td>
                          <td className="px-3 py-2 text-slate-400">—</td>
                        </tr>
                      ))}
                      {r.retraits.map((t) => (
                        <tr key={t.id} data-testid={`retrait-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-data text-xs uppercase tracking-[0.06em] text-red-700">Retrait</td>
                          <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{t.id}</td>
                          <td className="px-3 py-2 font-data text-sm text-slate-700">{t.date}</td>
                          <td className="px-3 py-2 text-sm text-slate-700">{t.cuve}</td>
                          <td className="px-3 py-2 font-data text-sm text-slate-400">—</td>
                          <td className="px-3 py-2 font-data text-sm text-slate-400">—</td>
                          <td className="tabular px-3 py-2 font-data text-sm font-semibold text-red-700">-{fmt(t.litres, 1)}</td>
                          <td className="px-3 py-2 font-data text-xs text-slate-500">Enlevé</td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              data-testid={`bon-sortie-${t.id}`}
                              onClick={() =>
                                setBonSortie({
                                  ...t,
                                  cuveNom: t.cuveNom ?? t.cuve ?? '—',
                                  solde: t.soldeApres ?? r.solde,
                                })
                              }
                              className="flex h-8 items-center gap-1.5 border border-slate-300 px-2 font-heading text-xs font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                            >
                              <Printer size={13} /> Bon de sortie
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))
          )}
        </>
      )}

      {facture && <FactureTrituration releve={facture} onClose={() => setFacture(null)} onEncaisse={load} />}
      {livre && <GrandLivreTiers compte={livre} onClose={() => setLivre(null)} />}
      {paiement && <PaiementTiersDialog tiersId={paiement} onClose={() => setPaiement(null)} onDone={load} />}
      {conversionTiers && (
        <ConversionDialog
          cleInitiale={fetchDroitsHuile().find((d) => d.tiersId === conversionTiers && d.droitKg > 0.001)?.cle ?? ''}
          onClose={() => setConversionTiers(null)}
          onDone={load}
        />
      )}
      {bonSortie && <BonSortieHuile retrait={bonSortie} onClose={() => setBonSortie(null)} />}
    </div>
  );
}
